import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  CircularProgress,
  Alert,
  Autocomplete,
  Box,
  IconButton,
  ListSubheader,
  FormHelperText
} from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import theme from '../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import { ThemeProvider } from '@mui/material/styles';
import { set } from 'date-fns';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, clearDraft, hasDraft } from '../../utils/draftUtils';
import { useNotification } from '../../contexts/NotificationContext';

import { API_BASE_URL } from '../../lib/constants';
const ConfirmatoryPhaseForm = () => {
  const { showNotification } = useNotification();
  const signaturePadRef = useRef(null);
  const [submitStatus, setSubmitStatus] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [currentSignatureRole, setCurrentSignatureRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  const handleSignatureClose = () => {
    setSignatureDialogOpen(false);
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case 'entry_date_time':
        if (!value) error = "Date & Time is required";
        break;
      case 'bp':
        if (!value) error = "BP is required";
        break;
      case 'heart_rate':
        if (!value) error = "Heart Rate is required";
        break;
      case 'anaesthesia_type':
        if (!value) error = "Anesthesia type is required";
        break;
      case 'anaesthesiologist_name':
        if (!value) error = "Anaesthesiologist name is required";
        break;
      case 'doctor_name':
        if (!value) error = "Physician name is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
    return error === "";
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    validateField(name, value);
  };
  const [patient, setPatient] = useState({ uhid_no: '', name: '' });
  const [formData, setFormData] = useState(() => ({
    uhid_no: '',
    entry_date_time: '',
    bp: '',
    heart_rate: '',
    time: '',
    oxygen_levels: '',
    temperature: '',
    last_medication_taken: '',
    dosage: '',
    fasting_confirmation: false,
    fasting_last_time: '',
    covid_screening: false,
    covid_screening_date: '',
    Comments_if_pending: '',
    blood_transfusion_consent: false,
    imaging_tests_reviewed: false,
    all_lab_results_verified: false,
    cardiac_clearance: false,
    imaging_tests: '',
    anaesthesia_type: '',
    pre_anaesthetic_medication_given: false,
    pre_anaesthetic_medication_details: '',
    patient_sedation_level: false,
    anaesthesiologist_clearance: false,
    anaesthesiologist_name: '',
    anaesthesiologist_signature: '',
    surgical_team_assigned: '',
    surgical_instruments_ready: false,
    ot_sterilization_check: false,
    blood_products_available: false,
    guardian_consent: false,
    anaesthesia_consent: false,
    legal_representative_name: '',
    legal_representative_contact: '',
    consent_date_time: '',
    antibiotics_given: false,
    antibiotics_drug_name: '',
    antibiotics_dosage: '',
    antibiotics_time: '',
    anti_coagulants_stopped: false,
    skin_prep_completed: false,
    units_reserved: '',
    pre_surgical_clothing_checked: false,
    blood_type: '',
    blood_cross_matching: '',
    allergy_review: '',
    blood_units_reserved: '',
    blood_units_reserved1: false,
    specify_allergies: '',
    final_physician_approval: false,
    doctor_name: '',
    doctor_signature: ''
  }));

  const navigate = useNavigate();
  const { uhid_no } = useParams(); // Extract uhid_no from params
  const [success, setSuccess] = useState(false);

  const handleChange = (event) => {
    const { name, type, checked } = event.target;
    let value = type === 'checkbox' ? checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    // Special logic: when fasting_confirmation is checked, set fasting_last_time to now
    if (name === 'fasting_confirmation' && checked) {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        fasting_last_time: new Date().toISOString().slice(0, 16),
      }));
      return;
    }
  };
  const handleNameChange = (e) => {
    const regex = /^[a-zA-Z\s.-]*$/; // allow letters, spaces, dot, hyphen
    const value = e.target.value;
    if (value === '' || regex.test(value)) {
      setFormData({ ...formData, [e.target.name]: value });
      if (errors[e.target.name]) {
        setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
      }
    }
  };


  // Auto-load draft if present on mount
  useEffect(() => {
    if (uhid_no && hasDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no);
      if (saved && saved.formData) {
        // Saved draft may contain patientData and rows; merge conservatively
        const { patientData: pd, rows: savedRows, ...rest } = saved.formData;
        if (pd) {
          setFormData(prev => ({ ...prev, ...pd }));
        }

      }
    }
  }, [uhid_no]);

  const handleSignatureOpen = (role) => {
    setCurrentSignatureRole(role);
    setSignatureDialogOpen(true);
  };
  const handleSignatureclose = () => {
    setSignatureDialogOpen(false)
  }
  const handleSignatureClear = () => {
    signaturePadRef.current.clear();
  };
  const handleDialogClose = () => {
    setSignatureDialogOpen(false);
  };
  const handleSignatureSave = () => {
    const dataURL = signaturePadRef.current.toDataURL();
    setFormData((prevData) => ({
      ...prevData,
      [currentSignatureRole]: dataURL.split(',')[1],
    }));
    setSignatureDialogOpen(false);
  };

  useEffect(() => {
    if (uhid_no) {
      fetchPatientData(uhid_no);
      fetchData(uhid_no);
      // Load draft if exists
      const draft = loadDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no);
      if (draft && draft.formData) {
        setFormData(draft.formData);
        // If draft has patient name, set it
        if (draft.formData.name) {
          setPatient(prev => ({ ...prev, name: draft.formData.name }));
        }
        console.log('Draft loaded for Confirmation Phase');
      }
    }
  }, [uhid_no]);

  const fetchData = async (uhidNo) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_confirmation_phase/${uhidNo}`);
      const backendData = response.data;
      console.log(response.data)
      const stripSeconds = (time) => {
        if (!time) return "";
        const parts = time.split(":");
        return parts.length >= 2 ? `${parts[0].padStart(2, "0")}:${parts[1]}` : time;
      };

      // Check if backendData exists and has a required field (like entry_date_time)
      if (
        backendData &&
        backendData.entry_date_time &&  // <--- important check
        Object.keys(backendData).length > 0
      ) {
        setFormData((prevData) => ({
          ...prevData,
          ...backendData,
          uhid_no: uhidNo,
          blood_units_reserved: backendData.blood_units_reserved,
          blood_cross_matching: backendData.blood_cross_matching,
          covid_screening: backendData.covid_screening,
          time: stripSeconds(backendData.time),

          covid_screening_date: backendData.covid_screening_date
            ? new Date(backendData.covid_screening_date).toISOString().slice(0, 16)
            : "",
        }));
        setSuccess(true);  // We have existing data, show "Update"
      } else {
        setSuccess(false); // No data, so show "Submit"
      }
    } catch (error) {
      console.error("Error fetching confirmation phase data:", error);
      setSuccess(false);
    }
  };

  const fetchPatientData = async (uhidNo) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/get_patient/${uhidNo}`);
      setPatient(response.data);
      // Always set patient name in formData for display
      setFormData(prev => ({
        ...prev,
        uhid_no: response.data.uhid_no,
        name: response.data.name || '',
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      setError('Failed to fetch patient data. Please try again.');
      setLoading(false);
    }
  };
  const bpOptions = [
    "120/80 (Normal)",
    "130/85 (Prehypertension)",
    "140/90 (Stage 1 Hypertension)",
    "160/100 (Stage 2 Hypertension)",
    "180/120 (Hypertensive Crisis)",
    "Low BP - Below 90/60",
    "Normal",
    "High",
  ];
  const pendingCommentsOptions = [
    'Waiting for equipment sterilization',
    'Awaiting surgical instruments',
    'Staff not available',
    'Patient preparation incomplete',
    'Pending anesthesia clearance',
  ];
  const unitsOptions = [
    '1 unit',
    '2 units',
    '3 units',
    '4 units',
    '5 units',
    '10 units',
  ];
  const dosageOptions = [
    '250 mg',
    '500 mg',
    '750 mg',
    '1 g',
    '2 g',
    '5 ml',
    '10 ml',
    '1 tablet',
    '2 tablets',
  ];
  const antibioticOptions = [
    'Amoxicillin',
    'Ciprofloxacin',
    'Azithromycin',
    'Doxycycline',
    'Cephalexin',
    'Clindamycin',
    'Metronidazole',
    'Erythromycin',
    'Penicillin',
    'Trimethoprim',
  ];
  const allergyOptions = [
    'Penicillin',
    'Aspirin',
    'Latex',
    'Peanuts',
    'Shellfish',
    'Bee Stings',
    'Pollen',
    'Dust',
    'None',
  ];
  const preAnaestheticDrugs = [
    'Midazolam 2mg',
    'Fentanyl 50mcg',
    'Propofol 100mg',
    'Ketamine 50mg',
    'Atropine 0.5mg',
    'Dexmedetomidine 1mcg/kg',
    'None',
  ];
  const imagingTestOptions = [
    "X-Ray",
    "Ultrasound",
    "CT Scan",
    "MRI",
    "PET Scan",
    "Mammography",
    "Bone Density Scan",
    "Fluoroscopy",
    "Nuclear Medicine Scan",
    "Echocardiogram",
  ];
  const surgicalTeamOptions = [
    'Dr. Smith - Lead Surgeon',
    'Dr. Patel - Assistant Surgeon',
    'Nurse Johnson - Scrub Nurse',
    'Nurse Lee - Circulating Nurse',
    'Anesthesiologist Dr. Kumar',
    'Technician Garcia',
    'None',
  ];
  const oxygenLevelOptions = [
    "95%",
    "96%",
    "97%",
    "98%",
    "99%",
    "100%",
    "Below 90% (Low - Hypoxemia)",
  ];
  const temperatureOptions = [
    "35.0 °C (Hypothermia)",
    "36.0 °C (Low Normal)",
    "36.5 °C (Normal)",
    "37.0 °C (Normal)",
    "37.5 °C (Slightly Elevated)",
    "38.0 °C (Fever)",
    "39.0 °C (High Fever)",
    "40.0 °C (Very High Fever)",
  ];
  const medicationOptions = [
    "Paracetamol",
    "Ibuprofen",
    "Amoxicillin",
    "Metformin",
    "Aspirin",
    "Atorvastatin",
    "Lisinopril",
    "Omeprazole",
    "Salbutamol",
    "Levothyroxine",
  ];

  const heartRateOptions = [
    "50 (Bradycardia - Slow)",
    "60 (Normal Lower Limit)",
    "70 (Normal Resting)",
    "80 (Normal Resting)",
    "90 (Elevated)",
    "100 (Tachycardia - Fast)",
    "110 (High)",
    "120 (Very High)",
  ];

  const doctorGroups = {
    Cardiologists: [
      "Dr. John Smith",
      "Dr. Emily Johnson",
      "Dr. Michael Brown",
      "Dr. Sarah Davis",
      "Dr. William Wilson",
      "Dr. Linda Martinez"
    ],
    Neurologists: [
      "Dr. Robert Lee",
      "Dr. Patricia Taylor",
      "Dr. James Anderson",
      "Dr. Barbara Thomas",
      "Dr. David Jackson",
      "Dr. Susan White"
    ],
    Orthopedics: [
      "Dr. Richard Garcia",
      "Dr. Jennifer Harris",
      "Dr. Charles Clark",
      "Dr. Jessica Lewis",
      "Dr. Thomas Robinson",
      "Dr. Karen Walker"
    ],
    Dermatologists: [
      "Dr. Nancy Young",
      "Dr. Matthew Hall",
      "Dr. Donna Allen",
      "Dr. Steven King",
      "Dr. Michelle Wright",
      "Dr. George Scott"
    ],
    Gynecologists: [
      "Dr. Sandra Green",
      "Dr. Kevin Adams",
      "Dr. Laura Baker",
      "Dr. Mark Nelson",
      "Dr. Angela Carter",
      "Dr. Brian Mitchell"
    ],
    Pediatricians: [
      "Dr. Melissa Perez",
      "Dr. Eric Roberts",
      "Dr. Deborah Turner",
      "Dr. Jason Phillips",
      "Dr. Rebecca Campbell",
      "Dr. Andrew Parker"
    ]
  };
  const [savedData, setSavedData] = useState(null);
  const [showSaveNotification, setShowSaveNotification] = useState(false);
  const [currentPage, setCurrentPage] = useState('form');
  const [dataLoaded, setDataLoaded] = useState(false);
  // Auto-save functionality
  // Auto-save functionality - only after initial data is loaded
  useEffect(() => {
    if (!dataLoaded) return; // Don't auto-save until initial data is loaded

    const timeoutId = setTimeout(() => {
      if (hasFormData()) {
        saveFormData(formData);
        setShowSaveNotification(true);
        setTimeout(() => setShowSaveNotification(false), 2000);
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);
  // Load saved data on component mount

  // Save form data to sessionStorage
  const saveFormData = (data) => {
    sessionStorage.setItem(`confirmationForm_${uhid_no}`, JSON.stringify(data));
    console.log('Form data saved:', data);
  };

  // Get saved form data
  const getSavedFormData = () => {
    const saved = sessionStorage.getItem(`confirmationForm_${uhid_no}`);
    return saved ? JSON.parse(saved) : null;
  };
  useEffect(() => {
    const saved = getSavedFormData();
    if (saved && Object.keys(saved).length > 0) {
      setSavedData(saved);
      setFormData(saved);
    }
  }, []);
  // Check if form has any data
  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(subValue =>
          typeof subValue === 'string' ? subValue.trim() !== '' : subValue
        );
      }
      return value === true;
    });
  };

  // Clear saved data - ONLY called on successful submit
  const clearSavedData = () => {
    setSavedData(null);
    sessionStorage.removeItem(`confirmationForm_${uhid_no}`);
    sessionStorage.removeItem(`confirmationForm_${uhid_no}`);
  };

  // Home button handler
  const handleHomeNavigation = () => {
    if (hasFormData()) {
      saveFormData(formData);
      // Store form status for home page to show "Continue" button
      sessionStorage.setItem(`formStatus_postOp_${uhid_no}`, JSON.stringify({
        formName: 'Confirmatory Phase (Pre-Op)Digitalization Checklist',
        uhid_no: uhid_no,
        status: 'incomplete',
        lastSaved: new Date().toISOString(),
        continueUrl: `/confirmationPhase/${uhid_no}`
      }));
    }
    navigate('/home');
  };
  const validate = () => {
    const tempErrors = {};

    // Always-required fields
    const alwaysRequired = [
      { key: 'entry_date_time', label: 'Surgery Date & Time' },
      { key: 'bp',              label: 'BP (Blood Pressure)'  },
      { key: 'heart_rate',      label: 'Heart Rate'           },
      { key: 'anaesthesia_type', label: 'Anesthesia Type'     },
      { key: 'doctor_name',     label: 'Final Physician Name' },
    ];

    alwaysRequired.forEach(({ key, label }) => {
      if (!formData[key] || String(formData[key]).trim() === '') {
        tempErrors[key] = `${label} is required`;
      }
    });

    // Anaesthesiologist Name is only required when the clearance checkbox is ticked
    // (the field is hidden when clearance is unchecked)
    if (formData.anaesthesiologist_clearance) {
      if (!formData.anaesthesiologist_name || String(formData.anaesthesiologist_name).trim() === '') {
        tempErrors.anaesthesiologist_name = 'Anaesthesiologist Name is required';
      }
    }

    setErrors(tempErrors);

    if (Object.keys(tempErrors).length > 0) {
      // Scroll to the first error
      const firstKey = Object.keys(tempErrors)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }

      // Build human-readable list of missing fields
      const labelMap = {
        entry_date_time: 'Surgery Date & Time',
        bp: 'BP (Blood Pressure)',
        heart_rate: 'Heart Rate',
        anaesthesia_type: 'Anesthesia Type',
        anaesthesiologist_name: 'Anaesthesiologist Name',
        doctor_name: 'Final Physician Name',
      };
      const missingList = Object.keys(tempErrors).map(k => labelMap[k] || k);
      showNotification('error', missingList);
    }

    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    // Date validations
    try {
      // COVID-19 screening must not be a future date-time
      if (formData.covid_screening && formData.covid_screening_date) {
        const selected = new Date(formData.covid_screening_date);
        const now = new Date();
        if (selected > now) {
          showNotification('error', 'COVID-19 screening date/time cannot be in the future.');
          return;
        }
      }
    } catch (e) {
      // In case of invalid date parsing, block submit
      showNotification('error', 'Please provide a valid COVID-19 screening date/time.');
      return;
    }

    // Prepare data, convert booleans to 0/1, exclude 'name' as it's not part of ConfirmationPhase model
    const { name, ...formDataWithoutName } = formData;
    const data = {
      ...formDataWithoutName,
      hospital_id: 1,
      fasting_confirmation: formData.fasting_confirmation ? 1 : 0,
      covid_screening: formData.covid_screening ? 1 : 0,
      blood_transfusion_consent: formData.blood_transfusion_consent ? 1 : 0,
      imaging_tests_reviewed: formData.imaging_tests_reviewed ? 1 : 0,
      all_lab_results_verified: formData.all_lab_results_verified ? 1 : 0,
      cardiac_clearance: formData.cardiac_clearance ? 1 : 0,
      pre_anaesthetic_medication_given: formData.pre_anaesthetic_medication_given ? 1 : 0,
      patient_sedation_level: formData.patient_sedation_level ? 1 : 0,
      anaesthesiologist_clearance: formData.anaesthesiologist_clearance ? 1 : 0,
      surgical_instruments_ready: formData.surgical_instruments_ready ? 1 : 0,
      ot_sterilization_check: formData.ot_sterilization_check ? 1 : 0,
      blood_products_available: formData.blood_products_available ? 1 : 0,
      guardian_consent: formData.guardian_consent ? 1 : 0,
      anaesthesia_consent: formData.anaesthesia_consent ? 1 : 0,
      antibiotics_given: formData.antibiotics_given ? 1 : 0,
      anti_coagulants_stopped: formData.anti_coagulants_stopped ? 1 : 0,
      skin_prep_completed: formData.skin_prep_completed ? 1 : 0,
      pre_surgical_clothing_checked: formData.pre_surgical_clothing_checked ? 1 : 0,
      allergy_review: formData.allergy_review ? 1 : 0,
      time: formData.time ? formData.time : null,
      fasting_last_time: formData.fasting_last_time ? formData.fasting_last_time : null,
      covid_screening_date: formData.covid_screening_date ? formData.covid_screening_date : null,
      consent_date_time: formData.consent_date_time ? formData.consent_date_time : null,
      antibiotics_time: formData.antibiotics_time ? formData.antibiotics_time : null,
      blood_cross_matching: formData.blood_cross_matching ? formData.blood_cross_matching : null,
      blood_units_reserved1: formData.blood_units_reserved1 ? 1 : 0,
      blood_units_reserved: formData.blood_units_reserved ? formData.blood_units_reserved : null,
      blood_type: formData.blood_type ? formData.blood_type : null,
      final_physician_approval: formData.final_physician_approval ? 1 : 0,
      anaesthesia_type: formData.anaesthesia_type ? formData.anaesthesia_type : null,
    };

    try {
      if (success) {
        await axios.put(`${API_BASE_URL}/update_confirmation_phase/${uhid_no}`, data);
        showNotification('success', "Confirmation Phase Form updated successfully!");

      } else {
        await axios.post(`${API_BASE_URL}/confirmation_phase`, data);
        setSuccess(true); // <-- After first submit, mark as success so button changes
        showNotification('success', 'Confirmation Phase Form submitted successfully!');

      }
      clearSavedData();
      // Clear draft after successful submission
      clearDraft(DRAFT_KEYS.CONFIRMATION_PHASE, uhid_no);
      navigate(`/mainPage/${formData.uhid_no}`, {
        state: { from: 'PreOperativePhase', status: 'success', step: 1 },
      });
    } catch (error) {
      console.error('Error submitting the form!', error);
      showNotification('error', `Error submitting form: ${error.message}`);
    }
  };

  if (loading && !patient.name) {
    return (
      <Container maxWidth="md" sx={{ marginTop: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate(`/mainPage/${formData.uhid_no}`, {
              state: { from: 'PreOperativePhase', status: 'success', step: 1 },
            })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            {/* Heading with Icon */}
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >

              Confirmatory Phase (Pre-Op)<br></br> Digitalization Checklist
            </Typography>

            {/* Right-aligned Logo and Text */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>




          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            {/* Left-aligned Patient Info */}
            <Typography variant="body1" sx={{ color: '#008080' }}>
              <b>UHID:</b> {patient.uhid_no || formData.uhid_no} &nbsp; &nbsp; <b>Patient Name:</b> {patient.name || formData.name || '-'}
            </Typography>

            {/* Right-aligned Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ marginRight: 1 }}
              >
                Home
              </Button>

            </Box>
          </Box>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  name='uhid_no'
                  label='UHID No.'
                  InputProps={{ readOnly: true, }}
                  variant='outlined'
                  fullWidth
                  value={formData.uhid_no}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Surgery Date & Time <span style={{ color: 'red' }}>*</span></span>}
                  name="entry_date_time"
                  type="datetime-local"
                  value={formData.entry_date_time}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  error={!!errors.entry_date_time}
                  helperText={errors.entry_date_time || ' '}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: new Date().toISOString().slice(0, 16),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={bpOptions}
                  value={formData.bp}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      bp: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<span>BP <span style={{ color: 'red' }}>*</span></span>}
                      name="bp"
                      fullWidth
                      onBlur={handleBlur}
                      error={!!errors.bp}
                      helperText={errors.bp || ' '}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={heartRateOptions}
                  value={formData.heart_rate}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      heart_rate: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={<span>Heart Rate (bpm) <span style={{ color: 'red' }}>*</span></span>}
                      name="heart_rate"
                      fullWidth
                      onBlur={handleBlur}
                      error={!!errors.heart_rate}
                      helperText={errors.heart_rate || ' '}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={oxygenLevelOptions}
                  value={formData.oxygen_levels}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      oxygen_levels: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Oxygen Levels (%)"
                      name="oxygen_levels"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={temperatureOptions}
                  value={formData.temperature}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      temperature: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Temperature (°C)"
                      name="temperature"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={medicationOptions}
                  value={formData.last_medication_taken}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      last_medication_taken: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Last Medication Taken"
                      name="last_medication_taken"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={dosageOptions}
                  value={formData.dosage}
                  onInputChange={(event, newInputValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      dosage: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Dosage"
                      name="dosage"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.fasting_confirmation} onChange={handleChange} name="fasting_confirmation" />}
                  label="Fasting Confirmation"
                />
                <TextField
                  label="Last Meal Time"
                  name="fasting_last_time"
                  type="datetime-local"
                  value={formData.fasting_last_time}
                  onChange={handleChange}
                  disabled={!formData.fasting_confirmation}
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    max: new Date().toISOString().slice(0, 16)
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.covid_screening} onChange={handleChange} name="covid_screening" />}
                  label="COVID-19 Screening"
                />
                <TextField
                  label="Test Date"
                  name="covid_screening_date"
                  type="datetime-local"
                  value={formData.covid_screening_date}
                  onChange={handleChange}
                  disabled={!formData.covid_screening}
                  fullWidth
                  inputProps={{
                    max: new Date().toISOString().slice(0, 16)
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.imaging_tests_reviewed} onChange={handleChange} name="imaging_tests_reviewed" />}
                  label="Imaging Tests Reviewed"
                />
                <Autocomplete
                  freeSolo
                  options={imagingTestOptions}
                  value={formData.imaging_tests}
                  disabled={!formData.imaging_tests_reviewed}
                  onInputChange={(event, newInputValue) => {
                    if (formData.imaging_tests_reviewed) {
                      setFormData(prev => ({
                        ...prev,
                        imaging_tests: newInputValue,
                      }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tests"
                      name="imaging_tests"
                      fullWidth
                      disabled={!formData.imaging_tests_reviewed}
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.all_lab_results_verified} onChange={handleChange} name="all_lab_results_verified" />}
                  label="All Pre-Surgical Lab Results Verified"
                />
                <FormControlLabel
                  control={<Checkbox checked={formData.cardiac_clearance} onChange={handleChange} name="cardiac_clearance" />}
                  label="Cardiac Clearance Attached"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.anaesthesia_type}>
                  <InputLabel id="anaesthesia-type-label">
                    Anesthesia Type Selected <span style={{ color: 'red' }}>*</span>
                  </InputLabel>
                  <Select
                    labelId="anaesthesia-type-label"
                    label="Anesthesia Type Selected *"
                    name="anaesthesia_type"
                    value={formData.anaesthesia_type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value=""><em>Select type</em></MenuItem>
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Spinal">Spinal</MenuItem>
                    <MenuItem value="Local">Local</MenuItem>
                    <MenuItem value="Epidural">Epidural</MenuItem>
                  </Select>
                  <FormHelperText>{errors.anaesthesia_type || ' '}</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pre_anaesthetic_medication_given}
                      onChange={handleChange}
                      name="pre_anaesthetic_medication_given"
                    />
                  }
                  label="Pre-Anesthesia Medication Given"
                />

                {formData.pre_anaesthetic_medication_given && (
                  <Autocomplete
                    freeSolo
                    options={preAnaestheticDrugs}
                    value={formData.pre_anaesthetic_medication_details}
                    onInputChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        pre_anaesthetic_medication_details: newValue,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Specify Drug & Dosage"
                        name="pre_anaesthetic_medication_details"
                        fullWidth
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.patient_sedation_level} onChange={handleChange} name="patient_sedation_level" />}
                  label="Patient Sedation Level Check"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.anaesthesiologist_clearance} onChange={handleChange} name="anaesthesiologist_clearance" />}
                  label={
                    <span>
                      Anesthesiologist Clearance
                      <span style={{ color: '#008080', fontSize: '0.75rem', marginLeft: 6 }}>
                        {formData.anaesthesiologist_clearance ? '▼ Fill doctor name below' : '▶ Tick to assign doctor'}
                      </span>
                    </span>
                  }
                />
                {formData.anaesthesiologist_clearance && (
                  <>
                    <Grid container spacing={2}>
                      {[
                        {
                          label: "Doctor",
                          key: "anaesthesiologist_signature",
                          key1: "anaesthesiologist_name",
                        }
                      ].map((person, index) => (
                        <Grid item xs={12} sm={6} key={index}>

                          <FormControl fullWidth error={!!errors[person.key1]}>
                            <InputLabel id={`select-label-${person.key1}`}>
                              Name ({person.label}) <span style={{ color: 'red' }}>*</span>
                            </InputLabel>

                            <Select
                              labelId={`select-label-${person.key1}`}
                              name={person.key1}
                              value={formData[person.key1] || ""}
                              onChange={handleNameChange}
                              onBlur={handleBlur}
                              label={`Name (${person.label}) *`}
                            >
                              <MenuItem value="">
                                <em>Select Doctor</em>
                              </MenuItem>

                              {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                                <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                                ...doctors.map((doctor, idx) => (
                                  <MenuItem key={`${specialty}-${idx}`} value={doctor}>
                                    {doctor}
                                  </MenuItem>
                                ))
                              ])}
                            </Select>
                            <FormHelperText>{errors[person.key1] || ' '}</FormHelperText>
                          </FormControl>
                          <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                          {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`} alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
                        </Grid>
                      ))}
                    </Grid>
                    <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
                      <DialogTitle>Provide Your Signature</DialogTitle>
                      <DialogContent>
                        <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
                        <Button onClick={handleSignatureSave} >Done</Button>
                        <Button onClick={handleSignatureClose} color="secondary">Close</Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Surgical Team Assigned "
                  name="surgical_team_assigned"
                  value={formData.surgical_team_assigned}
                  onChange={handleNameChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.surgical_instruments_ready} onChange={handleChange} name="surgical_instruments_ready" />}
                  label="Surgical Instruments & Equipment Ready"
                />
                {formData.surgical_instruments_ready === false && (
                  <Autocomplete
                    freeSolo
                    options={pendingCommentsOptions}
                    value={formData.Comments_if_pending}
                    onInputChange={(event, newInputValue) => {
                      setFormData(prev => ({
                        ...prev,
                        Comments_if_pending: newInputValue,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Comments if Pending"
                        name="Comments_if_pending"
                        fullWidth
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.ot_sterilization_check} onChange={handleChange} name="ot_sterilization_check" />}
                  label="OT Sterilization & Infection Control Check Completed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.blood_products_available} onChange={handleChange} name="blood_products_available" />}
                  label="Blood Products Available"
                />
                {formData.blood_products_available && (
                  <Autocomplete
                    freeSolo
                    options={unitsOptions}
                    value={formData.units_reserved}
                    onInputChange={(event, newInputValue) => {
                      setFormData(prev => ({
                        ...prev,
                        units_reserved: newInputValue,
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Units Reserved"
                        name="units_reserved"
                        fullWidth
                      />
                    )}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.guardian_consent} onChange={handleChange} name="guardian_consent" />}
                  label="Patient/Guardian Consent for Surgery Signed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.anaesthesia_consent} onChange={handleChange} name="anaesthesia_consent" />}
                  label="Anesthesia Consent Signed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.blood_transfusion_consent} onChange={handleChange} name="blood_transfusion_consent" />}
                  label="Blood Transfusion Consent Signed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Legal Representative Name"
                  name="legal_representative_name"
                  value={formData.legal_representative_name}
                  onChange={handleNameChange}
                  fullWidth
                  sx={{ marginBottom: 2 }}
                />
                <TextField
                  label="Legal Representative Contact"
                  name="legal_representative_contact"
                  value={formData.legal_representative_contact}
                  onChange={(e) => {
                    // Only allow digits, block all non-numeric input
                    const value = e.target.value.replace(/[^\d]/g, '');
                    setFormData(prev => ({
                      ...prev,
                      legal_representative_contact: value,
                    }));
                  }}
                  inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]*' }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Consent Date & Time"
                  name="consent_date_time"
                  type="datetime-local"
                  value={formData.consent_date_time}
                  onChange={handleChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.antibiotics_given} onChange={handleChange} name="antibiotics_given" />}
                  label="Antibiotics Given"
                />
                {formData.antibiotics_given && (
                  <>
                    <Autocomplete
                      freeSolo
                      options={antibioticOptions}
                      value={formData.antibiotics_drug_name}
                      onInputChange={(event, newInputValue) => {
                        setFormData(prev => ({
                          ...prev,
                          antibiotics_drug_name: newInputValue,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Drug Name"
                          name="antibiotics_drug_name"
                          fullWidth
                          sx={{ marginBottom: 2 }}
                        />
                      )}
                    />
                    <Autocomplete
                      freeSolo
                      options={dosageOptions}
                      value={formData.antibiotics_dosage}
                      onInputChange={(event, newInputValue) => {
                        setFormData(prev => ({
                          ...prev,
                          antibiotics_dosage: newInputValue,
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Dosage"
                          name="antibiotics_dosage"
                          fullWidth
                          sx={{ marginBottom: 2 }}
                        />
                      )}
                    />
                    <TextField
                      label="Date & Time"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true
                      }}
                      name='antibiotics_time'

                      value={formData.antibiotics_time}
                      onChange={handleChange}
                      fullWidth
                    />
                  </>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.anti_coagulants_stopped} onChange={handleChange} name="anti_coagulants_stopped" />}
                  label="Anti-Coagulants Stopped as per Guidelines"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.skin_prep_completed} onChange={handleChange} name="skin_prep_completed" />}
                  label="Skin Preparation Completed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.pre_surgical_clothing_checked} onChange={handleChange} name="pre_surgical_clothing_checked" />}
                  label="Pre-Surgical Clothing & Identification Band Checked"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel id="blood-cross-label">Blood Type & Cross-Matching</InputLabel>
                  <Select
                    labelId="blood-cross-label"
                    label="Blood Type & Cross-Matching"
                    name="blood_cross_matching"
                    value={formData.blood_cross_matching}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Blood Type</InputLabel>
                  <Select
                    name="blood_type"
                    value={formData.blood_type}
                    onChange={handleChange}
                    label="Blood Type"
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="A+">A+</MenuItem>
                    <MenuItem value="A-">A-</MenuItem>
                    <MenuItem value="B+">B+</MenuItem>
                    <MenuItem value="B-">B-</MenuItem>
                    <MenuItem value="AB+">AB+</MenuItem>
                    <MenuItem value="AB-">AB-</MenuItem>
                    <MenuItem value="O+">O+</MenuItem>
                    <MenuItem value="O-">O-</MenuItem>
                  </Select>
                </FormControl>


              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Allergy Review</InputLabel>
                  <Select
                    label="Blood Units Reserved"
                    name="blood_units_reserved"
                    value={formData.blood_units_reserved}
                    onChange={handleChange}

                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>

                <FormControlLabel
                  control={<Checkbox checked={formData.allergy_review} onChange={handleChange} name="allergy_review" />}
                  label="Allergy Review"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  freeSolo
                  options={allergyOptions}
                  value={formData.specify_allergies}
                  onInputChange={(event, newInputValue) => {
                    setFormData(prev => ({
                      ...prev,
                      specify_allergies: newInputValue,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Specify Allergies"
                      name="specify_allergies"
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>

                <Typography>Final Physician Approval</Typography>
                <br></br>
                <Grid container spacing={2}>

                  {[
                    {
                      label: "Doctor",
                      key: "doctor_signature",
                      key1: "doctor_name",
                    }
                  ].map((person, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <FormControl fullWidth error={!!errors[person.key1]}>
                        <InputLabel id={`select-label-${person.key1}`}>
                          Name ({person.label}) <span style={{ color: 'red' }}>*</span>
                        </InputLabel>

                        <Select
                          labelId={`select-label-${person.key1}`}
                          name={person.key1}
                          value={formData[person.key1] || ""}
                          onChange={handleNameChange}
                          onBlur={handleBlur}
                          label={`Name (${person.label}) *`}
                        >
                          <MenuItem value="">
                            <em>Select Doctor</em>
                          </MenuItem>

                          {Object.entries(doctorGroups).map(([specialty, doctors]) => [
                            <ListSubheader key={specialty}>{specialty}</ListSubheader>,
                            ...doctors.map((doctor, idx) => (
                              <MenuItem key={`${specialty}-${idx}`} value={doctor}>
                                {doctor}
                              </MenuItem>
                            ))
                          ])}
                        </Select>
                        <FormHelperText>{errors[person.key1] || ' '}</FormHelperText>
                      </FormControl>

                      <Button variant="outlined" color="primary" sx={{ marginTop: 2 }} onClick={() => handleSignatureOpen(person.key)}> Sign Here </Button>
                      {formData[person.key] && <img src={`data:image/png;base64,${formData[person.key]}`} alt="Signature" style={{ marginTop: 10, border: '1px solid black', width: '100%', height: '150px' }} />}
                    </Grid>
                  ))}
                </Grid>
                <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)}>
                  <DialogTitle>Provide Your Signature</DialogTitle>
                  <DialogContent>
                    <SignatureCanvas ref={signaturePadRef} canvasProps={{ width: 400, height: 200, className: 'sigCanvas' }} />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleSignatureClose} >close</Button>
                    <Button onClick={handleSignatureClear} color="secondary">Clear</Button>
                    <Button onClick={handleSignatureSave} color="primary">Done</Button>
                  </DialogActions>
                </Dialog>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, marginTop: 3 }}>

                  <DraftSaveButton
                    formKey={DRAFT_KEYS.CONFIRMATION_PHASE}
                    uhidNo={uhid_no}
                    formData={formData}
                    onDraftLoaded={(draftData) => setFormData(draftData)}
                    variant="outlined"
                    size="large"
                    isUpdate={success}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                  >
                    {success ? 'Update' : 'Submit'}
                  </Button>

                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConfirmatoryPhaseForm;
